// SME Capture Flow — Phase 5 stage edit helpers (pure).
//
// Stages partition the ordered timeline into consecutive groups. The expert
// confirms or adjusts what the system proposed: rename a stage, or nudge the
// boundary between two stages so an event changes sides. Pure array ops,
// unit-tested without rendering (tests/captureStages.test.ts).

export interface StageEntry {
  id: string;
  label: string;
  eventIds: string[];
  turningPoint: string | null;
}

const withTurningPoint = (s: StageEntry): StageEntry => ({ ...s, turningPoint: s.eventIds[0] ?? null });

// Replace the label of the stage with the given id (immutably).
export function renameStage(stages: StageEntry[], id: string, label: string): StageEntry[] {
  return stages.map((s) => (s.id === id ? { ...s, label } : s));
}

// Move the boundary ABOVE stage `index` (so index >= 1):
//  - 'up'   → the stage starts one event earlier (absorbs the previous stage's last event)
//  - 'down' → the stage starts one event later (gives its first event to the previous stage)
// Invalid moves (first stage, or a move that would empty either side) return
// the same array reference so callers can wire buttons without guards.
export function moveStageBoundary(stages: StageEntry[], index: number, dir: 'up' | 'down'): StageEntry[] {
  if (index <= 0 || index >= stages.length) return stages;
  const prev = stages[index - 1];
  const curr = stages[index];

  if (dir === 'up') {
    if (prev.eventIds.length < 2) return stages; // would empty the previous stage
    const moved = prev.eventIds[prev.eventIds.length - 1];
    const nextPrev = withTurningPoint({ ...prev, eventIds: prev.eventIds.slice(0, -1) });
    const nextCurr = withTurningPoint({ ...curr, eventIds: [moved, ...curr.eventIds] });
    return stages.map((s, i) => (i === index - 1 ? nextPrev : i === index ? nextCurr : s));
  }

  if (curr.eventIds.length < 2) return stages; // would empty this stage
  const moved = curr.eventIds[0];
  const nextPrev = withTurningPoint({ ...prev, eventIds: [...prev.eventIds, moved] });
  const nextCurr = withTurningPoint({ ...curr, eventIds: curr.eventIds.slice(1) });
  return stages.map((s, i) => (i === index - 1 ? nextPrev : i === index ? nextCurr : s));
}
